import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Search, ShieldCheck } from "lucide-react";
import { searchRegistry } from "@/lib/registry/registry.functions";
import { registryPath } from "@/lib/registry/registry";
import { RegistryChip } from "@/components/registry/registry-chip";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { RouteErrorBoundary, RouteNotFoundBoundary } from "@/components/route-boundaries";

const TITLE = "Verified Card Registry Search | BidNova Auctions";
const DESCRIPTION =
  "Search BidNova's Verified Card Registry by certificate number, card name or set to see the permanent verification record for a graded trading card.";
const URL = "https://bidnovaauctions.uk/registry";

const PROVIDERS = ["psa", "bgs", "cgc", "sgc", "ace", "tag"] as const;

export const Route = createFileRoute("/registry/")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  errorComponent: RouteErrorBoundary,
  notFoundComponent: RouteNotFoundBoundary,
  component: RegistrySearchPage,
});

type Row = {
  id: string;
  provider: string;
  certificate_number: string;
  card_name: string | null;
  set_name: string | null;
  year: number | null;
  grade: string | null;
  card_number: string | null;
  status: string;
  verification_status: string;
};

function RegistrySearchPage() {
  const search = useServerFn(searchRegistry);
  const [term, setTerm] = useState("");
  const [provider, setProvider] = useState<string>("all");
  const [submitted, setSubmitted] = useState<{ query: string; provider: string } | null>(null);

  const q = useQuery({
    queryKey: ["registry-search", submitted],
    enabled: submitted !== null,
    queryFn: () =>
      search({
        data: {
          query: submitted?.query || undefined,
          provider: submitted?.provider !== "all" ? submitted?.provider : undefined,
          limit: 24,
        },
      }) as Promise<Row[]>,
  });

  return (
    <main className="mx-auto w-full max-w-4xl px-4 py-10">
      <header className="mb-6">
        <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest text-emerald-300">
          <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" /> BidNova Verified Card Registry
        </p>
        <h1 className="mt-2 text-2xl font-black sm:text-3xl">Search the registry</h1>
        <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
          Look up a certificate number, card name or set to see its permanent verification record,
          market activity and population data. Certificate numbers are matched without spaces or
          dashes.
        </p>
      </header>

      <form
        className="grid gap-2 sm:grid-cols-[minmax(0,1fr)_auto_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          setSubmitted({ query: term.trim(), provider });
        }}
      >
        <Input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Cert number, card name or set"
          aria-label="Search the Verified Card Registry"
          maxLength={120}
        />
        <Select value={provider} onValueChange={setProvider}>
          <SelectTrigger className="sm:w-36" aria-label="Grading company">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All graders</SelectItem>
            {PROVIDERS.map((p) => (
              <SelectItem key={p} value={p}>
                {p.toUpperCase()}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button type="submit" className="min-h-11">
          <Search className="mr-1.5 h-4 w-4" aria-hidden="true" /> Search
        </Button>
      </form>

      <section className="mt-8" aria-live="polite">
        {q.isFetching && <p className="text-sm text-muted-foreground">Searching the registry…</p>}
        {submitted && !q.isFetching && (q.data?.length ?? 0) === 0 && (
          <Card className="p-6 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">No registry record found</p>
            <p className="mt-1">
              Records are created when a certificate is verified on BidNova. If this card has never
              been verified here, there is nothing to show yet — that is not a sign the certificate
              is fake.
            </p>
          </Card>
        )}
        <ul className="grid gap-3">
          {(q.data ?? []).map((r) => (
            <li key={r.id}>
              <Link
                to={registryPath(r.provider, r.certificate_number)}
                className="block rounded-lg border border-border/60 bg-surface-2/40 p-4 transition-colors hover:border-gold/40"
              >
                <div className="grid grid-cols-[minmax(0,1fr)_auto] items-start gap-3 sm:flex sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-medium">{r.card_name ?? "Graded card"}</p>
                    <p className="mt-0.5 truncate text-xs text-muted-foreground">
                      {[r.year, r.set_name, r.card_number ? `#${r.card_number}` : null]
                        .filter(Boolean)
                        .join(" · ")}
                    </p>
                    <p className="mt-1 font-mono text-[11px] text-muted-foreground">
                      {r.provider.toUpperCase()} · {r.certificate_number}
                      {r.grade ? ` · Grade ${r.grade}` : ""}
                    </p>
                  </div>
                  <RegistryChip
                    provider={r.provider}
                    certNumber={r.certificate_number}
                    status={r.status}
                    verificationStatus={r.verification_status}
                    asLink={false}
                  />
                </div>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    </main>
  );
}
